import type { ReactNode } from 'react';
import { Logo } from '@/components/Logo';
import { ProfileMenu } from '@/components/ProfileMenu';
import { useCurrentUserContext } from '@/contexts/CurrentUserContext';
import {
  Flex,
  Grid,
  GridItem,
  type FlexProps,
  Container,
  Text,
  type ContainerProps,
} from '@/ui';
import type { LogoHref } from './types';
import { getLogoLinkProps, NAV_HEIGHT } from './utils';

type Props = FlexProps & {
  logoHref?: LogoHref;
  logoVariant?: 'pink' | 'black';
  logoHeight?: number | Record<string, number>;
  showBrandText?: boolean;
  leftExtra?: ReactNode;
  middleColumn?: ReactNode;
  rightColumn?: JSX.Element | 'default' | 'none';
  containerProps?: ContainerProps;
};

/**
 * Header is used on:
 * - Magic/dashboard tabs
 * - Public snippet pages
 * - Auth screens
 * - Experience/page paths
 */
export const SlimHeader = ({
  logoHref = 'default',
  logoVariant = 'pink',
  logoHeight = 32,
  showBrandText = true,
  leftExtra,
  middleColumn,
  rightColumn,
  containerProps,
  ...props
}: Props) => {
  const { isAuthenticated } = useCurrentUserContext();
  const logoLinkProps = getLogoLinkProps(logoHref, isAuthenticated);

  return (
    <Flex h={NAV_HEIGHT} w="100%" {...props}>
      <Container
        alignItems="center"
        as={Grid}
        fontWeight="medium"
        gap={{ base: 3, md: 6 }}
        variant="max"
        gridTemplateColumns={
          middleColumn === undefined ? '1fr auto' : '1fr auto 1fr'
        }
        {...containerProps}
      >
        <GridItem align="center" as={Flex} gap="2.5" minW={0}>
          <Flex
            align="center"
            flexShrink={0}
            {...logoLinkProps}
            _hover={{ textDecor: 'none' }}
          >
            <Logo height={logoHeight} variant={logoVariant} />
          </Flex>
          {showBrandText && (
            <Text
              {...logoLinkProps}
              _hover={{ textDecor: 'none' }}
              fontSize="17px"
              fontWeight="extrabold"
              display={{
                base: middleColumn || leftExtra ? 'none' : 'initial',
                md: 'initial',
              }}
            >
              agendahero
            </Text>
          )}
          {leftExtra}
        </GridItem>

        {middleColumn !== undefined && <GridItem>{middleColumn}</GridItem>}

        <GridItem align="center" as={Flex} gap="2" justify="flex-end" minW={0}>
          {rightColumn === 'default' ? (
            <ProfileMenu />
          ) : rightColumn === 'none' ? null : (
            rightColumn
          )}
        </GridItem>
      </Container>
    </Flex>
  );
};
